import React, { useState, useEffect } from 'react';
import {
	Loader2,
	CheckCircle,
	Plus,
	Minus,
	PlayCircle,
	ChevronLeft,
	ChevronRight,
} from 'lucide-react';
import ReviewSection from '../components/ReviewSection';

// Cashfree SDK Hook
const useCashfreeSDK = () => {
	const [sdkLoaded, setSdkLoaded] = useState(false);
	const [cashfree, setCashfree] = useState(null);

	useEffect(() => {
		const existingScript = document.querySelector('script[src*="cashfree"]');

		if (existingScript && window.Cashfree) {
			const cf = window.Cashfree({ mode: 'production' });
			setCashfree(cf);
			setSdkLoaded(true);
			return;
		}

		const script = document.createElement('script');
		script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
		script.async = true;

		script.onload = () => {
			if (window.Cashfree) {
				const cf = window.Cashfree({ mode: 'production' });
				setCashfree(cf);
				setSdkLoaded(true);
			}
		};

		script.onerror = () => console.error('❌ Failed to load Cashfree SDK');
		document.body.appendChild(script);

		return () => {
			if (script.parentNode) script.parentNode.removeChild(script);
		};
	}, []);

	const openCheckout = (sessionId) => {
		if (!cashfree || !sdkLoaded) return false;

		try {
			cashfree.checkout({
				paymentSessionId: sessionId,
				redirectTarget: '_self',
			});
			return true;
		} catch (error) {
			return false;
		}
	};

	return { sdkLoaded, openCheckout };
};

const OrderKgvHelmet = () => {
	const [formData, setFormData] = useState({
		customerName: '',
		customerEmail: '',
		customerPhone: '',
		helmetColor: 'black',
		quantity: 1,
	});

	const [otpSent, setOtpSent] = useState(false);
	const [otpVerified, setOtpVerified] = useState(false);
	const [otpCode, setOtpCode] = useState('');
	const [otpLoading, setOtpLoading] = useState(false);
	const [otpError, setOtpError] = useState('');

	const sendHelmetOtp = async () => {
		setOtpLoading(true);
		setOtpError('');
		try {
			const cleanPhone = formData.customerPhone.replace(/\s+/g, '');
			const res = await fetch('https://auth.kgvl.co.in/api/otp/send', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ phone: cleanPhone }),
			});
			const data = await res.json();
			if (data.success) {
				setOtpSent(true);
			} else {
				setOtpError(data.message || 'Failed to send OTP');
			}
		} catch (err) {
			setOtpError(err.message || 'Error sending OTP');
		} finally {
			setOtpLoading(false);
		}
	};

	const verifyHelmetOtp = async () => {
		setOtpLoading(true);
		setOtpError('');
		try {
			const cleanPhone = formData.customerPhone.replace(/\s+/g, '');
			const res = await fetch('https://auth.kgvl.co.in/api/otp/verify', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ phone: cleanPhone, otp: otpCode }),
			});
			const data = await res.json();
			if (data.success) {
				setOtpVerified(true);
			} else {
				setOtpError(data.message || 'Invalid OTP');
			}
		} catch (err) {
			setOtpError(err.message || 'Invalid OTP');
		} finally {
			setOtpLoading(false);
		}
	};

	const [isLoading, setIsLoading] = useState(false);
	const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
	const [paymentStatus, setPaymentStatus] = useState('idle');
	const [showSuccessModal, setShowSuccessModal] = useState(false);
	const [orderDetails, setOrderDetails] = useState(null);
	const { sdkLoaded, openCheckout } = useCashfreeSDK();

	const helmetColors = [
		{
			id: 'black',
			name: 'Premium Black Helmet',
			price: 3900,
			originalPrice: 8000,
			images: [
				'/black1.jpeg',
				'/black2.jpeg',
				'/black3.jpeg',
				'/black4.jpeg',
				'/black5.jpeg',
				'/black6.jpeg',
			],
			video:
				'https://res.cloudinary.com/dp06wptmx/video/upload/v1767675728/demo_j6yt2q.mp4',
		},
		{
			id: 'green',
			name: 'Olive Green Edition Helmet',
			price: 3900,
			originalPrice: 8000,
			images: [
				'/green1.jpeg',
				'/green2.jpeg',
				'/green3.jpeg',
				'/green4.jpeg',
				'/green5.jpeg',
				'/green6.jpeg',
			],
			video: '/video5.mp4',
		},
	];

	const selectedHelmet = helmetColors.find(
		(h) => h.id === formData.helmetColor
	);

	// Combine images and video into one media array
	const mediaItems = React.useMemo(() => {
		if (!selectedHelmet) return [];
		const items = selectedHelmet.images.map((url) => ({ type: 'image', url }));
		if (selectedHelmet.video) {
			items.push({ type: 'video', url: selectedHelmet.video });
		}
		return items;
	}, [selectedHelmet]);

	const currentMedia = mediaItems[currentMediaIndex];

	// Price calculations
	const basePrice = (selectedHelmet?.price || 0) * formData.quantity;
	const originalPrice =
		(selectedHelmet?.originalPrice || 0) * formData.quantity;
	const discountPercentage = selectedHelmet
		? Math.round(
			((selectedHelmet.originalPrice - selectedHelmet.price) /
				selectedHelmet.originalPrice) *
			100
		)
		: 0;
	const totalPrice = basePrice;

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const orderId = params.get('order_id');
		const status = params.get('status');

		if (orderId && status === 'success') {
			const storedData = localStorage.getItem('pendingHelmetOrderData');
			if (storedData) {
				const orderData = JSON.parse(storedData);
				if (orderData.orderId === orderId) {
					verifyPayment(orderId, orderData);
				}
			}
		}
	}, []);

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	const handleColorChange = (colorId) => {
		setFormData((prev) => ({ ...prev, helmetColor: colorId }));
		setCurrentMediaIndex(0);
	};

	const handleQuantityChange = (delta) => {
		setFormData((prev) => {
			const newQty = prev.quantity + delta;
			return newQty >= 1 ? { ...prev, quantity: newQty } : prev;
		});
	};

	const nextMedia = () => {
		setCurrentMediaIndex((prev) => (prev + 1) % mediaItems.length);
	};

	const prevMedia = () => {
		setCurrentMediaIndex(
			(prev) => (prev - 1 + mediaItems.length) % mediaItems.length
		);
	};

	const verifyPayment = async (orderId, storedData) => {
		try {
			setPaymentStatus('processing');
			const verifyResponse = await fetch(
				'https://auth.kgvl.co.in/api/helmet-orders/verify',
				{
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						orderId: orderId,
						customerName: storedData.customerName,
						customerPhone: storedData.customerPhone,
						customerEmail: storedData.customerEmail,
						helmetColor: storedData.helmetColor,
						quantity: storedData.quantity,
						address: storedData.address,
						orderAmount: storedData.totalPrice,
					}),
				}
			);

			const data = await verifyResponse.json();
			if (data.success) {
				setOrderDetails({
					orderId: orderId,
					paymentId: data.order?.transactionId || data.transactionId,
					amount: data.order?.amount || storedData.totalPrice,
					customerName: storedData.customerName,
					customerEmail: storedData.customerEmail,
					customerPhone: storedData.customerPhone,
					helmetColor: storedData.helmetColor,
					quantity: storedData.quantity,
					address: storedData.address,
					timestamp: new Date().toLocaleString(),
					paymentStatus: data.paymentStatus || 'SUCCESS',
				});
				setPaymentStatus('success');
				if (window.fbq) {
					window.fbq('track', 'Lead', {
						content_name: 'Premium Helmet',
						content_category: 'Accessories',
						value: storedData.totalPrice,
						currency: 'INR',
					});
				}
				setShowSuccessModal(true);
				localStorage.removeItem('pendingHelmetOrderData');
				window.history.replaceState(
					{},
					document.title,
					window.location.pathname
				);
			} else {
				setPaymentStatus('error');
			}
		} catch (error) {
			setPaymentStatus('error');
		}
	};

	const initiatePayment = async () => {
		if (!otpVerified) {
			alert('Please verify your mobile number first.');
			return;
		}
		const validations = [
			{
				check: formData.customerName.trim(),
				msg: 'Please enter your full name',
			},
			{
				check: formData.customerEmail.trim(),
				msg: 'Please enter your email address',
			},
			{
				check: formData.customerPhone.trim(),
				msg: 'Please enter your phone number',
			},
			{
				check: formData.address.trim(),
				msg: 'Please enter your delivery address',
			},
		];

		for (const v of validations) {
			if (!v.check) {
				alert(v.msg);
				return;
			}
		}

		const phoneNumber = formData.customerPhone.replace(/\s+/g, '');
		if (!/^[6-9]\d{9}$/.test(phoneNumber)) {
			alert('Invalid phone number');
			return;
		}

		setIsLoading(true);

		try {
			const orderResponse = await fetch(
				'https://auth.kgvl.co.in/api/helmet-orders/checkout',
				{
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						amount: totalPrice,
						customerName: formData.customerName,
						customerEmail: formData.customerEmail,
						customerPhone: phoneNumber,
						helmetColor: selectedHelmet.name,
						quantity: formData.quantity,
						address: formData.address,
					}),
				}
			);

			const data = await orderResponse.json();
			if (!data.success || !data.sessionId)
				throw new Error(data.message || 'Failed to create session');

			localStorage.setItem(
				'pendingHelmetOrderData',
				JSON.stringify({
					orderId: data.orderId,
					...formData,
					totalPrice,
					timestamp: Date.now(),
				})
			);

			openCheckout(data.sessionId);
		} catch (error) {
			alert(`Payment failed: ${error.message}`);
			setIsLoading(false);
		}
	};

	if (showSuccessModal && orderDetails) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 py-12 px-4">
				<div className="max-w-4xl mx-auto">
					<div className="bg-white rounded-2xl shadow-xl p-8 mb-6 text-center">
						<div className="inline-flex items-center justify-center w-24 h-24 bg-green-100 rounded-full mb-6">
							<CheckCircle className="w-16 h-16 text-green-600" />
						</div>
						<h1 className="text-4xl font-bold text-gray-900 mb-2">
							Order Confirmed!
						</h1>
						<p className="text-xl text-gray-600">
							Your helmet order has been successfully placed
						</p>
					</div>

					<div className="grid md:grid-cols-2 gap-6 mb-6">
						<div className="bg-white rounded-xl shadow-lg p-6">
							<h2 className="text-xl font-bold text-gray-900 mb-4">
								Order Summary
							</h2>
							<div className="space-y-3">
								<div>
									<p className="text-sm text-gray-500">Order ID</p>
									<p className="font-semibold">{orderDetails.orderId}</p>
								</div>
								<div>
									<p className="text-sm text-gray-500">Amount Paid</p>
									<p className="text-2xl font-bold text-green-600">
										₹{orderDetails.amount?.toLocaleString('en-IN')}
									</p>
								</div>
								<div>
									<p className="text-sm text-gray-500">Status</p>
									<span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
										{orderDetails.paymentStatus}
									</span>
								</div>
							</div>
						</div>
						<div className="bg-white rounded-xl shadow-lg p-6">
							<h2 className="text-xl font-bold text-gray-900 mb-4">
								Delivery Details
							</h2>
							<div className="space-y-3">
								<div>
									<p className="text-sm text-gray-500">Customer</p>
									<p className="font-semibold">{orderDetails.customerName}</p>
								</div>
								<div>
									<p className="text-sm text-gray-500">Address</p>
									<p className="text-sm font-medium">{orderDetails.address}</p>
								</div>
								<div>
									<p className="text-sm text-gray-500">Product</p>
									<p className="font-semibold">
										{orderDetails.helmetColor} × {orderDetails.quantity}
									</p>
								</div>
							</div>
						</div>
					</div>

					<button
						onClick={() => window.location.reload()}
						className="w-full bg-green-600 text-white px-6 py-4 rounded-lg hover:bg-green-700 transition-all font-semibold"
					>
						Place Another Order
					</button>
				</div>
			</div>
		);
	}

	if (paymentStatus === 'processing') {
		return (
			<div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 flex items-center justify-center p-4">
				<div className="bg-white rounded-2xl shadow-2xl p-12 max-w-md w-full text-center">
					<Loader2 className="w-20 h-20 text-green-600 animate-spin mx-auto mb-6" />
					<h2 className="text-2xl font-bold text-gray-800 mb-3">
						Verifying Your Payment
					</h2>
					<p className="text-gray-600">
						Please wait while we confirm your helmet order
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 py-8 sm:py-12 px-3 sm:px-4">
			<div className="max-w-6xl mx-auto p-4 sm:p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-16 items-start bg-white rounded-xl sm:rounded-2xl shadow-lg border border-green-100 mb-8">
				{/* Media Gallery Section */}
				<div className="flex flex-col items-start gap-4 sm:gap-6 w-full mx-auto md:mx-0">
					<div className="relative w-full aspect-square overflow-hidden rounded-lg sm:rounded-xl shadow-lg border border-gray-100">
						<div className="absolute inset-0 bg-green-200 blur-3xl opacity-20"></div>

						<div className="relative z-10 w-full h-full group">
							{currentMedia?.type === 'image' ? (
								<div className="relative w-full h-full">
									<img
										src={currentMedia.url}
										alt={`${selectedHelmet?.name} View`}
										className="w-full h-full object-cover"
									/>
									<div className="absolute bottom-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-xs">
										{currentMediaIndex + 1} / {mediaItems.length}
									</div>
								</div>
							) : currentMedia?.type === 'video' ? (
								<video
									controls
									autoPlay
									muted
									className="w-full h-full object-cover"
									poster={selectedHelmet?.images?.[0]}
								>
									<source src={currentMedia.url} type="video/mp4" />
								</video>
							) : (
								<div className="w-full h-full bg-gray-200 flex items-center justify-center">
									No Media
								</div>
							)}

							{/* Navigation Arrows */}
							<button
								onClick={prevMedia}
								title="Previous"
								className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-800/80 p-2 rounded-full shadow-md hover:shadow-xl transition-all z-20 active:scale-95 opacity-80 group-hover:opacity-100"
							>
								<ChevronLeft className="w-6 h-6" />
							</button>
							<button
								onClick={nextMedia}
								title="Next"
								className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-800/80 p-2 rounded-full shadow-md hover:shadow-xl transition-all z-20 active:scale-95 opacity-80 group-hover:opacity-100"
							>
								<ChevronRight className="w-6 h-6" />
							</button>
						</div>
					</div>

					{/* Thumbnails - Aligned Left and Wrapping */}
					<div className="flex flex-wrap justify-start gap-2 w-full pb-2 px-1">
						{mediaItems.map((item, idx) => (
							<button
								key={idx}
								onClick={() => setCurrentMediaIndex(idx)}
								className={`flex-shrink-0 relative w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${currentMediaIndex === idx
									? 'border-green-500 scale-105 shadow-md'
									: 'border-gray-200 hover:border-green-300'
									}`}
							>
								{item.type === 'image' ? (
									<img
										src={item.url}
										alt="Thumbnail"
										className="w-full h-full object-cover"
									/>
								) : (
									<div className="w-full h-full bg-gray-800 flex items-center justify-center relative">
										<img
											src={selectedHelmet?.images[0]}
											alt="Video Thumbnail"
											className="w-full h-full object-cover opacity-50"
										/>
										<PlayCircle className="absolute text-white w-6 h-6" />
									</div>
								)}
							</button>
						))}
					</div>

					{/* Color Selection */}
					<div className="w-full bg-gradient-to-br from-green-50 to-emerald-50 p-4 sm:p-5 rounded-xl border border-green-100">
						<h3 className="text-xs font-bold text-gray-700 mb-3 text-start uppercase tracking-wider">
							Choose Your Color
						</h3>
						<div className="flex flex-wrap justify-start gap-2">
							{helmetColors.map((helmet) => (
								<button
									key={helmet.id}
									onClick={() => handleColorChange(helmet.id)}
									className={`px-4 py-2 rounded-lg text-xs font-bold transition-all transform border-2 ${formData.helmetColor === helmet.id
										? 'bg-green-600 text-white border-transparent shadow-md'
										: 'bg-white text-gray-800 border-gray-200 hover:border-green-400'
										}`}
								>
									{helmet.name}
								</button>
							))}
						</div>
					</div>
				</div>

				{/* Product Information Section */}
				<div className="space-y-4 sm:space-y-6">
					<div>
						<h1 className="text-2xl sm:text-4xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
							{selectedHelmet?.name}
						</h1>
						<p className="text-xs text-gray-500 mt-1 uppercase tracking-widest font-semibold">
							By Karishma Global Ventures
						</p>
					</div>

					<p className="text-gray-700 leading-relaxed text-sm sm:text-base">
						Lightweight aerodynamic full-face helmet balancing comfort and
						safety. High-quality shell and premium lining for long rides.
					</p>

					<div className="grid grid-cols-2 gap-2">
						{[
							'🔊 Bluetooth connectivity',
							'📞 Hands-free calling',
							'🎧 Noise-canceling HD speakers',
							'🗺️ GPS navigation support',
							'🔋 Fast charging battery',
							'🧼 Washable inner padding',
						].map((feature, idx) => (
							<div
								key={idx}
								className="text-gray-700 text-xs sm:text-sm bg-gray-50 p-2 rounded-lg border border-gray-100"
							>
								{feature}
							</div>
						))}
					</div>

					<div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl border border-green-200">
						<div className="flex items-center gap-3 mb-1">
							<span className="text-lg text-gray-400 line-through">
								₹{selectedHelmet?.originalPrice?.toLocaleString('en-IN')}
							</span>
							<span className="bg-red-500 text-white px-2 py-0.5 rounded-full text-[10px] font-bold">
								{discountPercentage}% OFF
							</span>
						</div>
						<div className="text-3xl font-bold text-green-600">
							₹{(selectedHelmet?.price || 0).toLocaleString('en-IN')}/-
						</div>
						<p className="text-[10px] text-gray-500 mt-1 italic">
							inclusive of GST & standard shipping
						</p>
					</div>

					<div className="bg-blue-50 border border-blue-100 p-4 rounded-xl flex items-center justify-between gap-4">
						<div className="flex-1">
							<h3 className="text-lg font-bold text-blue-900 mb-1">
								Product Brochure
							</h3>
							<p className="text-sm text-blue-700">
								Detailed specs and gallery (PDF)
							</p>
						</div>
						<div className="flex gap-2">
							<a
								href="/helmet catalogue.pdf"
								target="_blank"
								className="bg-blue-600 text-white text-base px-3 py-2 rounded-md font-semibold hover:bg-blue-700 transition-colors"
							>
								VIEW
							</a>
							<a
								href="/helmet catalogue.pdf"
								download
								className="bg-green-600 text-white text-base px-3 py-2 rounded-md font-semibold hover:bg-green-700 transition-colors"
							>
								DOWNLOAD
							</a>
						</div>
					</div>
				</div>
			</div>

			{/* Form Section */}
			<div id="complete-your-order" className="bg-white rounded-xl shadow-2xl p-4 sm:p-8 max-w-6xl mx-auto border border-green-100">
				<h2 className="text-2xl font-bold text-center mb-8 bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
					Complete Your Order
				</h2>

				<div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
					<div className="space-y-4">
						<div>
							<label className="block mb-1 text-xs font-semibold text-gray-700">
								Full Name *
							</label>
							<input
								type="text"
								name="customerName"
								value={formData.customerName}
								onChange={handleInputChange}
								disabled={otpVerified}
								placeholder="Enter your full name"
								className="border border-gray-300 w-full rounded-lg px-4 py-2 text-sm focus:border-green-500 transition-all outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
							/>
						</div>

						<div>
							<label className="block mb-1 text-xs font-semibold text-gray-700">
								Phone Number *
							</label>
							<input
								type="text"
								name="customerPhone"
								value={formData.customerPhone}
								onChange={handleInputChange}
								disabled={otpVerified}
								placeholder="Enter 10-digit mobile number"
								className="border border-gray-300 w-full rounded-lg px-4 py-2 text-sm focus:border-green-500 transition-all outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
							/>
						</div>

						{/* OTP Verification UI */}
						{!otpVerified && (
							<div className="p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-3">
								{!otpSent ? (
									<button
										type="button"
										onClick={sendHelmetOtp}
										disabled={otpLoading || !/^[6-9]\d{9}$/.test(formData.customerPhone.replace(/\s+/g, ""))}
										className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-bold py-2 px-4 rounded-lg transition duration-200 text-sm flex items-center justify-center gap-2"
									>
										{otpLoading ? <Loader2 className="w-4 h-4 animate-spin text-white" /> : "Send OTP"}
									</button>
								) : (
									<div className="space-y-3">
										<p className="text-xs text-gray-500">An OTP has been sent to {formData.customerPhone}. Please enter it below:</p>
										<div className="flex gap-2">
											<input
												type="text"
												maxLength={6}
												placeholder="Enter 6-Digit OTP"
												value={otpCode}
												onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
												className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-800 placeholder-gray-400 text-sm focus:outline-none focus:border-green-500"
											/>
											<button
												type="button"
												onClick={verifyHelmetOtp}
												disabled={otpLoading || otpCode.length !== 6}
												className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-bold px-4 py-2 rounded-lg transition duration-200 text-sm flex items-center justify-center"
											>
												{otpLoading ? <Loader2 className="w-4 h-4 animate-spin text-white" /> : "Verify"}
											</button>
										</div>
										<div className="flex justify-between items-center text-xs">
											<button
												type="button"
												onClick={sendHelmetOtp}
												disabled={otpLoading}
												className="text-green-600 hover:underline"
											>
												Resend OTP
											</button>
											{otpError && <span className="text-red-600 font-semibold">{otpError}</span>}
										</div>
									</div>
								)}
							</div>
						)}

						{otpVerified && (
							<div className="text-xs text-green-700 font-semibold flex items-center gap-1.5 bg-green-50 border border-green-200 px-3 py-2.5 rounded-lg">
								<CheckCircle className="w-4 h-4 text-green-600" /> Phone Number Verified Successfully. You can now fill in the remaining details.
							</div>
						)}

						<div>
							<label className="block mb-1 text-xs font-semibold text-gray-700">
								Email Address *
							</label>
							<input
								type="email"
								name="customerEmail"
								value={formData.customerEmail}
								onChange={handleInputChange}
								disabled={!otpVerified}
								placeholder="your.email@example.com"
								className="border border-gray-300 w-full rounded-lg px-4 py-2 text-sm focus:border-green-500 transition-all outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
							/>
						</div>

						<div>
							<label className="block mb-1 text-xs font-semibold text-gray-700">
								Delivery Address *
							</label>
							<textarea
								name="address"
								value={formData.address}
								onChange={handleInputChange}
								disabled={!otpVerified}
								rows="3"
								placeholder="Enter your delivery address"
								className="border border-gray-300 w-full rounded-lg px-4 py-2 text-sm focus:border-green-500 transition-all outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
							></textarea>
						</div>

						<div className="flex items-center gap-4 py-2">
							<span className="text-xs font-semibold text-gray-700">
								Quantity
							</span>
							<div className="flex items-center gap-2">
								<button
									onClick={() => handleQuantityChange(-1)}
									disabled={!otpVerified}
									className="bg-red-50 text-red-600 border border-red-200 w-8 h-8 rounded-lg flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
								>
									-
								</button>
								<span className="font-bold w-6 text-center">
									{formData.quantity}
								</span>
								<button
									onClick={() => handleQuantityChange(1)}
									disabled={!otpVerified}
									className="bg-green-50 text-green-600 border border-green-200 w-8 h-8 rounded-lg flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
								>
									+
								</button>
							</div>
						</div>

						<button
							onClick={initiatePayment}
							disabled={isLoading || !sdkLoaded || !otpVerified}
							className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-4 rounded-xl shadow-lg transition-all active:scale-95 disabled:opacity-50 mt-4 flex items-center justify-center gap-2"
						>
							{isLoading ? (
								<Loader2 className="animate-spin" />
							) : (
								`PAY ₹${totalPrice.toLocaleString('en-IN')}`
							)}
						</button>
					</div>

					<div className="bg-gray-50 p-6 rounded-xl border border-gray-200 h-fit">
						<h3 className="font-bold text-gray-800 mb-4 pb-2 border-b border-gray-200">
							Price Summary
						</h3>
						<div className="space-y-3 text-sm">
							<div className="flex justify-between">
								<span>Unit Price:</span>
								<span className="font-semibold text-gray-500 line-through">
									₹{selectedHelmet?.originalPrice?.toLocaleString('en-IN')}
								</span>
							</div>
							<div className="flex justify-between">
								<span>Sale Price:</span>
								<span className="font-semibold text-green-600">
									₹{selectedHelmet?.price?.toLocaleString('en-IN')}
								</span>
							</div>
							<div className="flex justify-between">
								<span>Quantity:</span>
								<span className="font-semibold">{formData.quantity}</span>
							</div>
							<div className="flex justify-between font-bold text-red-600 pt-2">
								<span>Savings:</span>
								<span>
									- ₹{(originalPrice - basePrice).toLocaleString('en-IN')}
								</span>
							</div>
							<div className="flex justify-between text-lg font-bold border-t border-gray-200 pt-3">
								<span>Total:</span>
								<span className="text-green-600">
									₹{totalPrice.toLocaleString('en-IN')}
								</span>
							</div>
						</div>
						<div className="mt-6 p-4 bg-white border border-gray-200 rounded-lg">
							<p className="text-[10px] text-gray-500 font-semibold mb-2">
								🚚 SHIPPING INFO
							</p>
							<p className="text-xs text-gray-700">
								Fast delivery within 3-5 business days across India.
							</p>
						</div>
					</div>
				</div>
			</div>

			{/* Customer Reviews Section */}
			<ReviewSection showForm={false} />
		</div>
	);
};

export default OrderKgvHelmet;
